import * as Sentry from "@sentry/react";
import "@github/spark/spark";
import { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from "react-error-boundary";

import App from './App.tsx';
import { ErrorFallback } from './ErrorFallback.tsx';
import './i18n'; // Initialize i18n

import "./index.css";
import "./main.css";
import "./styles/theme.css";

// Initialize Sentry as early as possible in the application lifecycle
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN || "https://ee05d2a26d1aacf2c9849b5db370039c@o4507401003466752.ingest.de.sentry.io/4510736272785488",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  // Set environment based on mode
  environment: import.meta.env.MODE,
  // Enable performance monitoring
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
  // Adjust this value in production to reduce volume.
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // Only send errors in production by default
  enabled: import.meta.env.PROD || import.meta.env.VITE_SENTRY_ENABLED === 'true',
});

// Register service worker for PWA support
// Delay registration to avoid conflicts with Spark initialization
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    // Add a small delay to ensure Spark is fully initialized
    setTimeout(() => {
      navigator.serviceWorker
        .register('/sw.js')
        .then((registration) => {
          console.log('[PWA] Service Worker registered:', registration.scope);
          
          // Check for updates periodically
          setInterval(() => {
            registration.update();
          }, 60 * 60 * 1000); // Check every hour
        })
        .catch((error) => {
          console.error('[PWA] Service Worker registration failed:', error);
        });
    }, 100); // Small delay for Spark initialization
  });
}

// Create root element with better error handling
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found. Check index.html');
}

createRoot(rootElement).render(
  <ErrorBoundary FallbackComponent={ErrorFallback}>
    <Suspense fallback={
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading FlowHR...</p>
        </div>
      </div>
    }>
      <App />
    </Suspense>
   </ErrorBoundary>
)
